import { useMemo } from "react";

import { DropdownDataItem } from "@/components/ui/dropdown/types";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  AttributeOption,
  DatasetFilter,
  EFilterOptionType,
  FilterOption
} from "@/types/dataset";

import { twMerge } from "tailwind-merge";

import DateFilter from "./DateFilter";
import DateTimeFilter from "./DateTimeFilter";
import MinMaxFilter from "./MinMaxFilter";
import MultipleSelectionFilter from "./MultipleSelectionFilter";
import TimeFilter from "./TimeFilter";
import { DateRange, DateTimeRange, MinMaxRange, TimeRange } from "./types";

type AttributeFilterProps = {
  savedFilters?: DatasetFilter[];
  isEditMode?: boolean;
  getAvailableFilterOptions: () => DropdownDataItem<AttributeOption>;
  handleSelectFilter: (option: AttributeOption | null) => void;
  selectAttributeItem?: AttributeOption | null;
  defaultSelectedValue?: AttributeOption | null;
  attributeValues?: FilterOption[];
  searchTerm?: string;
  selectedOptions?: string[];
  isGettingAttributeValues?: boolean;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChange: (
    data:
      | FilterOption[]
      | DateTimeRange
      | DateRange
      | TimeRange
      | MinMaxRange
      | string
  ) => void;
};

export const AttributeFilter = ({
  savedFilters = [],
  isEditMode = false,
  getAvailableFilterOptions,
  handleSelectFilter,
  selectAttributeItem,
  defaultSelectedValue,
  attributeValues,
  searchTerm = "",
  selectedOptions = [],
  onSearchChange,
  isGettingAttributeValues = false,
  onChange
}: AttributeFilterProps) => {
  const currentSelectedFilter = useMemo(
    () =>
      savedFilters.find(
        (filter) => filter.filterId === selectAttributeItem?.id
      ),
    [savedFilters, selectAttributeItem]
  );

  // Determine filter types
  const [
    isMultipleSelection,
    isDateTimeFilter,
    isDateFilter,
    isTimeFilter,
    isMinMaxFilter
  ] = useMemo(
    () => [
      selectAttributeItem?.type === EFilterOptionType.MULTIPLE,
      selectAttributeItem?.type === EFilterOptionType.DATE_TIME,
      selectAttributeItem?.type === EFilterOptionType.DATE,
      selectAttributeItem?.type === EFilterOptionType.TIME,
      selectAttributeItem?.type === EFilterOptionType.MIN_MAX
    ],
    [selectAttributeItem]
  );

  const attributeOptions = getAvailableFilterOptions();
  const activeOption = selectAttributeItem ?? defaultSelectedValue;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="space-y-3 flex-none mt-3">
        <div className="relative">
          <Select
            value={activeOption?.id ?? ""}
            onValueChange={(id) =>
              handleSelectFilter(
                attributeOptions.find((option) => option.id === id) ?? null
              )
            }
          >
            <SelectTrigger
              id="menu-button"
              data-testid="attribute-filter-trigger"
              aria-label="Dropdown menu: Attribute Filter"
              className="h-[40px] rounded-lg border-gainsboro text-xs text-txtHeading data-[state=open]:border-teal-500 data-[placeholder]:text-txtPlaceholder"
            >
              <SelectValue placeholder="Attribute Filter" />
            </SelectTrigger>
            <SelectContent className="max-h-[var(--radix-select-content-available-height)]">
              <SelectGroup>
                {attributeOptions.map((option) => (
                  <SelectItem
                    key={option.id}
                    value={option.id}
                    data-testid={`attribute-filter-option-${option.id}`}
                    textValue={option.label}
                    disabled={option.disabled}
                    className={twMerge(
                      "block w-full px-4 py-2 text-sm text-left text-gray-700",
                      "data-[disabled]:text-gray-400 data-[disabled]:cursor-not-allowed data-[disabled]:hover:bg-transparent data-[disabled]:pointer-events-auto",
                      !option.disabled && "hover:bg-gray-50 cursor-pointer"
                    )}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>
      {isMultipleSelection && (
        <MultipleSelectionFilter
          isEdit={isEditMode}
          isLoading={isGettingAttributeValues}
          options={attributeValues ?? []}
          selectedOptions={selectedOptions}
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          onSelectOption={onChange}
          onSelectAll={onChange}
        />
      )}
      {isDateTimeFilter && (
        <DateTimeFilter
          onSave={onChange}
          defaultValue={currentSelectedFilter?.dateTimeRange}
        />
      )}
      {isDateFilter && (
        <DateFilter
          onSave={onChange}
          defaultValue={currentSelectedFilter?.dateRange}
        />
      )}
      {isTimeFilter && (
        <TimeFilter
          onSave={onChange}
          defaultValue={currentSelectedFilter?.timeRange}
        />
      )}
      {isMinMaxFilter && (
        <MinMaxFilter
          type={selectAttributeItem?.data_type}
          onSave={onChange}
          defaultValue={currentSelectedFilter?.minMaxRange}
        />
      )}
    </div>
  );
};
